import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { analyticsAPI } from '../../services/api';
import { useDataSource } from '../../contexts/DataSourceContext';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LineChart,
  Line,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import {
  UsersIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  BuildingOfficeIcon,
  CalendarIcon,
  SparklesIcon,
  XMarkIcon,
  AdjustmentsHorizontalIcon,
  UserGroupIcon,
  PlayCircleIcon,
  ArchiveBoxIcon,
  ClipboardDocumentListIcon,
  FunnelIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import ClientTracking from './ClientTracking';
import SamplesAnalytics from '../dashboard/SamplesAnalytics';
import StockAnalytics from './analytics/StockAnalytics';
import ProductAnalytics from './analytics/ProductAnalytics';
import SupplierAnalytics from './analytics/SupplierAnalytics';
import { Card, CardBody, CardHeader, Spinner, StatCard, Badge, PageTransition, StaggerContainer, fadeInUpVariant } from '../ui';
import { motion, AnimatePresence } from 'framer-motion';
import { useSpring, animated, config } from 'react-spring';
import { formatCompactNumber, formatCurrency } from '../../utils/formatters';
import DonutNavigation, { SectionType } from './DonutNavigation';
import SectionContent from './SectionContent';
import { LiquidFillChart, RadialProgress, GaugeChart } from '../charts';
import { dashboardTheme } from '../../styles/dashboardTheme';

// Enhanced Sections with rich animated visualizations (ONE per subsection)
import EnhancedSections from './EnhancedSections';

// 3D Assets
// @ts-ignore
import dashboardHero from '../../assets/images/dashboard-hero.png';
// @ts-ignore
import iconUsers from '../../assets/images/icon-3d-users-v2.png';
// @ts-ignore
import iconDeals from '../../assets/images/icon-3d-deals-v2.png';
// @ts-ignore
import iconRevenue from '../../assets/images/icon-3d-revenue-v2.png';
// @ts-ignore
import iconTeam from '../../assets/images/icon-3d-team-v2.png';
// @ts-ignore
import iconTrophy from '../../assets/images/icon-3d-trophy-v2.png';

// Mock data for demo mode
const mockOverview = {
  totalRevenue: 2450000,
  totalClients: 156,
  totalDeals: 89,
  totalSalesmen: 12,
};

const mockSalesmanPerformance = [
  { name: 'John Smith', revenue: 450000, deals: 32, clients: 45 },
  { name: 'Sarah Johnson', revenue: 420000, deals: 28, clients: 38 },
  { name: 'Mike Davis', revenue: 280000, deals: 22, clients: 28 },
  { name: 'Emily Brown', revenue: 350000, deals: 25, clients: 35 },
];

const mockDealsByPeriod = [
  { period: 'Week 1', deals: 12, revenue: 450000 },
  { period: 'Week 2', deals: 18, revenue: 620000 },
  { period: 'Week 3', deals: 15, revenue: 580000 },
  { period: 'Week 4', deals: 22, revenue: 800000 },
];

const mockClientsByPeriod = [
  { period: 'Week 1', clients: 8 },
  { period: 'Week 2', clients: 12 },
  { period: 'Week 3', clients: 10 },
  { period: 'Week 4', clients: 15 },
];

const mockRevenueTrends = [
  { month: 'Jan', revenue: 1200000 },
  { month: 'Feb', revenue: 1450000 },
  { month: 'Mar', revenue: 1680000 },
  { month: 'Apr', revenue: 1920000 },
  { month: 'May', revenue: 2100000 },
  { month: 'Jun', revenue: 2450000 },
];

const mockTopPerformers = {
  topSalesman: { name: 'John Smith', revenue: 450000 },
  topDeal: { client: 'Acme Corp', amount: 125000 },
  topClient: { name: 'TechStart Inc', totalRevenue: 380000 },
};

const mockActivitySummary = {
  totalActivities: 245,
  completedActivities: 189,
  pendingActivities: 56,
  overdueActivities: 12,
};

const mockStockAnalytics = {
  totalProducts: 156,
  lowStockItems: 23,
  outOfStockItems: 5,
  totalValue: 1250000,
};

const mockProductAnalytics = {
  topProducts: [
    { name: 'Premium Widget Pro', revenue: 450000, units: 1200 },
    { name: 'Enterprise Solution', revenue: 680000, units: 450 },
    { name: 'Professional Suite', revenue: 420000, units: 890 },
  ],
  totalRevenue: 2450000,
  totalUnitsSold: 8500,
};

const mockSupplierAnalytics = {
  totalSuppliers: 45,
  activeSuppliers: 38,
  totalSpent: 850000,
  avgDealSize: 22368,
};

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { isDemoMode } = useDataSource();
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('7d');
  const [overview, setOverview] = useState<any>(null);
  const [salesmanPerformance, setSalesmanPerformance] = useState<any[]>([]);
  const [dealsByPeriod, setDealsByPeriod] = useState<any[]>([]);
  const [clientsByPeriod, setClientsByPeriod] = useState<any[]>([]);
  const [revenueTrends, setRevenueTrends] = useState<any[]>([]);
  const [topPerformers, setTopPerformers] = useState<any>(null);
  const [activitySummary, setActivitySummary] = useState<any>(null);
  const [stockAnalytics, setStockAnalytics] = useState<any>(null);
  const [productAnalytics, setProductAnalytics] = useState<any>(null);
  const [supplierAnalytics, setSupplierAnalytics] = useState<any>(null);

  // Custom date range state
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [activeCustomRange, setActiveCustomRange] = useState<{ start: string; end: string } | null>(null);

  // Navigation state
  const [activeSection, setActiveSection] = useState<SectionType>('activity');
  const [activeSubsection, setActiveSubsection] = useState<string | null>(null);



  // Spring animation for paper unroll - MUST be at top level before any conditional returns
  const paperSpring = useSpring({
    from: { scaleX: 0, opacity: 0 },
    to: { scaleX: 1, opacity: 1 },
    config: { ...config.slow, tension: 120, friction: 30 },
    reset: true,
    key: `${selectedPeriod}-${activeCustomRange?.start || ''}`,
  });

  // Navigation handlers
  const handleSectionChange = (section: SectionType) => {
    setActiveSection(section);
    setActiveSubsection(null);
  };

  const handleSubsectionClick = (subsectionId: string) => {
    console.log('🔍 Subsection clicked:', subsectionId);
    setActiveSubsection(subsectionId);

    // The actual HTML element ID has '-section' suffix
    const sectionElementId = `${subsectionId}-section`;
    console.log('🎯 Looking for element with ID:', sectionElementId);

    setTimeout(() => {
      const element = document.getElementById(sectionElementId);
      console.log('📍 Element found:', element ? 'YES' : 'NO');

      if (element) {
        console.log('📜 Attempting to scroll to element...');

        // Use scrollIntoView which is more reliable
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });

        // Adjust for fixed header offset
        setTimeout(() => {
          const offset = 120;
          const currentScroll = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
          console.log('🔧 Current scroll position:', currentScroll);
          console.log('🔧 Adjusting by offset:', offset);

          window.scrollTo({
            top: currentScroll - offset,
            behavior: 'smooth',
          });
        }, 100);
      } else {
        console.error(`❌ Element with ID "${sectionElementId}" not found`);
        console.log('🔎 Available section IDs:',
          Array.from(document.querySelectorAll('[id*="section"]')).map(el => el.id)
        );
      }
    }, 100);
  };

  const periods = [
    { value: '1d', label: '1 Day' },
    { value: '7d', label: '7 Days' },
    { value: '1m', label: '1 Month' },
    { value: '1y', label: '1 Year' },
    { value: 'custom', label: 'Custom' },
  ];



  useEffect(() => {
    fetchData();
  }, [selectedPeriod, activeCustomRange, isDemoMode]);

  useEffect(() => {
    // Listen for data source changes
    const handleDataSourceChange = () => {
      fetchData();
    };

    window.addEventListener('dataSourceChanged', handleDataSourceChange);
    return () => {
      window.removeEventListener('dataSourceChanged', handleDataSourceChange);
    };
  }, [selectedPeriod, activeCustomRange, isDemoMode]);

  const fetchData = async () => {
    try {
      // Only show full loading on initial load
      if (isInitialLoad) {
        // Keep as is - full loading spinner
      } else {
        setDataLoading(true);
      }

      console.log('🔄 AdminDashboard fetchData - isDemoMode:', isDemoMode);

      if (isDemoMode) {
        // Use mock data in demo mode
        console.log('📊 Using DEMO data');
        setOverview(mockOverview);
        setSalesmanPerformance(mockSalesmanPerformance);
        setDealsByPeriod(mockDealsByPeriod);
        setClientsByPeriod(mockClientsByPeriod);
        setRevenueTrends(mockRevenueTrends);
        setTopPerformers(mockTopPerformers);
        setActivitySummary(mockActivitySummary);
        setStockAnalytics(mockStockAnalytics);
        setProductAnalytics(mockProductAnalytics);
        setSupplierAnalytics(mockSupplierAnalytics);
      } else {
        console.log('🌐 Fetching LIVE data from backend');
        // Fetch real data in live mode
        // Determine date params
        const startDate = activeCustomRange?.start;
        const endDate = activeCustomRange?.end;
        const periodToUse = activeCustomRange ? undefined : selectedPeriod;

        const [
          overviewData,
          salesmanData,
          dealsData,
          clientsData,
          revenueData,
          topPerformersData,
          activityData,
          stockData,
          productData,
          supplierData,
        ] = await Promise.all([
          analyticsAPI.getOverview(),
          analyticsAPI.getSalesmanPerformance(periodToUse, startDate, endDate),
          analyticsAPI.getDealsByPeriod(periodToUse, startDate, endDate),
          analyticsAPI.getClientsByPeriod(periodToUse, startDate, endDate),
          analyticsAPI.getRevenueTrends(periodToUse, startDate, endDate),
          analyticsAPI.getTopPerformers(periodToUse, startDate, endDate),
          analyticsAPI.getActivitySummary(),
          analyticsAPI.getStockAnalytics(periodToUse, startDate, endDate),
          analyticsAPI.getProductAnalytics(periodToUse, startDate, endDate),
          analyticsAPI.getSupplierAnalytics(periodToUse, startDate, endDate),
        ]);

        console.log('✅ LIVE data fetched:', { overviewData, salesmanData });
        setOverview(overviewData);
        setSalesmanPerformance(salesmanData);
        setDealsByPeriod(dealsData);
        setClientsByPeriod(clientsData);
        setRevenueTrends(revenueData);
        setTopPerformers(topPerformersData);
        setActivitySummary(activityData);
        setStockAnalytics(stockData);
        setProductAnalytics(productData);
        setSupplierAnalytics(supplierData);
      }
    } catch (err: any) {
      console.error('❌ Error fetching data:', err);
      setError(err.response?.data?.message || 'Failed to fetch analytics data');
    } finally {
      setIsInitialLoad(false);
      setDataLoading(false);
    }
  };

  const handlePeriodChange = (value: string) => {
    if (value === 'custom') {
      setShowDatePicker(true);
    } else {
      setActiveCustomRange(null);
      // Always trigger data fetch even if same period is clicked
      if (value === selectedPeriod) {
        // Force refresh by directly calling fetchData
        setDataLoading(true);
        fetchData();
      } else {
        setSelectedPeriod(value);
      }
    }
  };

  const handleApplyCustomRange = () => {
    if (customStartDate && customEndDate) {
      setActiveCustomRange({ start: customStartDate, end: customEndDate });
      setSelectedPeriod('custom');
      setShowDatePicker(false);
    }
  };

  const formatDateLabel = () => {
    if (activeCustomRange) {
      const start = new Date(activeCustomRange.start).toLocaleDateString();
      const end = new Date(activeCustomRange.end).toLocaleDateString();
      return `${start} - ${end}`;
    }
    return periods.find(p => p.value === selectedPeriod)?.label || '7 Days';
  };

  // Initial loading - full spinner
  if (isInitialLoad && !overview) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8 flex items-center justify-center min-h-[400px]">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        <Card variant="bordered" className="border-error-200 bg-error-50">
          <CardBody className="p-4 text-sm text-error-600">{error}</CardBody>
        </Card>
      </div>
    );
  }

  return (
    <PageTransition className="px-4 sm:px-6 lg:px-8 py-8 bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30 min-h-screen">

      {/* 📜 Enhanced 3D Paper Roll Hero */}
      <motion.div
        id="activity-section"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="mb-8 relative"
      >
        {/* Paper Tab Period Selectors */}
        <div
          key={`tabs-${selectedPeriod}`}
          className="absolute -top-4 right-6 flex gap-2 z-30"
          style={{ perspective: '1000px' }}
        >
          {periods.map((period, idx) => (
            <motion.button
              key={period.value}
              initial={{ y: -40, opacity: 0, rotateX: -25 }}
              animate={{ y: 0, opacity: 1, rotateX: 0 }}
              transition={{ delay: 0.2 + idx * 0.08, duration: 0.6, type: 'spring', bounce: 0.3 }}
              whileHover={{ y: 3, scale: 1.03, rotateX: 2 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => handlePeriodChange(period.value)}
              className={`relative px-5 py-3 text-sm font-bold transition-all duration-300 ${selectedPeriod === period.value
                ? 'text-blue-900'
                : 'text-slate-600 hover:text-slate-800'
                }`}
              style={{
                background: selectedPeriod === period.value
                  ? 'linear-gradient(180deg, #ffffff 0%, #f8fafc 50%, #f1f5f9 100%)'
                  : 'linear-gradient(180deg, #f1f5f9 0%, #e2e8f0 100%)',
                borderRadius: '0 0 14px 14px',
                boxShadow: selectedPeriod === period.value
                  ? '0 10px 25px rgba(37, 99, 235, 0.3), 0 6px 12px rgba(0,0,0,0.12), inset 0 2px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(0,0,0,0.05)'
                  : '0 5px 10px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.6)',
                transformStyle: 'preserve-3d',
                border: selectedPeriod === period.value ? '1px solid rgba(37, 99, 235, 0.2)' : '1px solid rgba(148, 163, 184, 0.3)',
                borderTop: selectedPeriod === period.value ? '5px solid #2563eb' : '4px solid #94a3b8',
              }}
            >
              {/* Enhanced 3D paper edge effect */}
              <div
                className="absolute left-0 right-0 top-0 h-2 opacity-70"
                style={{
                  background: selectedPeriod === period.value
                    ? 'linear-gradient(180deg, rgba(37, 99, 235, 0.4) 0%, rgba(37, 99, 235, 0.1) 50%, transparent 100%)'
                    : 'linear-gradient(180deg, rgba(0,0,0,0.12) 0%, rgba(0,0,0,0.04) 50%, transparent 100%)',
                }}
              />
              {/* Paper fold highlight */}
              <div
                className="absolute left-0 right-0 bottom-0 h-px"
                style={{
                  background: selectedPeriod === period.value
                    ? 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.8) 50%, transparent 100%)'
                    : 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.5) 50%, transparent 100%)',
                }}
              />
              {period.value === 'custom' && activeCustomRange
                ? formatDateLabel()
                : period.label}
            </motion.button>
          ))}
        </div>

        {/* Enhanced Hero Card with Paper Roll Effect */}
        <div
          className="relative w-full overflow-hidden rounded-3xl hero-shimmer-card"
          style={{
            background: 'linear-gradient(135deg, #fafbfc 0%, #f0f4f8 50%, #f5f7fa 100%)',
          }}
        >
          {/* Enhanced shimmer sweep effect */}
          <div
            className="absolute inset-0 rounded-3xl pointer-events-none hero-shimmer-border"
            style={{
              background: 'linear-gradient(90deg, transparent 0%, transparent 35%, rgba(59, 130, 246, 0.15) 50%, transparent 65%, transparent 100%)',
              backgroundSize: '200% 100%',
            }}
          />

          {/* Dual ambient glow */}
          <div
            className="absolute inset-0 rounded-3xl pointer-events-none"
            style={{
              background: 'radial-gradient(ellipse at 0% 50%, rgba(59, 130, 246, 0.06) 0%, transparent 60%), radial-gradient(ellipse at 100% 50%, rgba(99, 102, 241, 0.04) 0%, transparent 60%)',
            }}
          />

          {/* CSS Keyframes */}
          <style>{`
            .hero-shimmer-border {
              animation: shimmerSweep 5s ease-in-out infinite;
            }
            @keyframes shimmerSweep {
              0%, 100% {
                background-position: -100% 0%;
              }
              50% {
                background-position: 200% 0%;
              }
            }
            .hero-shimmer-card {
              box-shadow:
                0 4px 6px -1px rgba(0, 0, 0, 0.04),
                0 10px 20px -3px rgba(0, 0, 0, 0.06),
                0 20px 30px -5px rgba(59, 130, 246, 0.08),
                inset 0 2px 0 rgba(255, 255, 255, 0.7),
                inset 0 -1px 0 rgba(0, 0, 0, 0.03);
              border: 1px solid rgba(59, 130, 246, 0.12);
            }
            @keyframes float {
              0%, 100% { transform: translateY(0px) translateX(0px); }
              33% { transform: translateY(-12px) translateX(4px); }
              66% { transform: translateY(-6px) translateX(-4px); }
            }
          `}</style>

          {/* Enhanced floating particles */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-3xl">
            {[...Array(6)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute rounded-full bg-gradient-to-br from-blue-400/20 to-indigo-400/20"
                style={{
                  width: `${4 + i * 2}px`,
                  height: `${4 + i * 2}px`,
                  left: `${15 + i * 15}%`,
                  top: `${25 + (i % 3) * 25}%`,
                  filter: 'blur(1px)',
                }}
                animate={{
                  y: [0, -15, 0],
                  x: [0, i % 2 === 0 ? 5 : -5, 0],
                  opacity: [0.15, 0.4, 0.15],
                  scale: [1, 1.1, 1],
                }}
                transition={{
                  duration: 5 + i * 0.5,
                  repeat: Infinity,
                  delay: i * 0.7,
                  ease: "easeInOut",
                }}
              />
            ))}
          </div>

          <div className="flex items-stretch">

            {/* Left Side - Enhanced 3D Cylinder Tube */}
            <motion.div
              className="relative w-24 lg:w-32 flex-shrink-0 flex items-center justify-center"
              initial={{ rotateY: -15 }}
              animate={{ rotateY: 0 }}
              transition={{ duration: 0.8, type: 'spring' }}
              style={{
                background: 'linear-gradient(90deg, #1e3a8a 0%, #1e40af 15%, #2563eb 35%, #3b82f6 50%, #2563eb 65%, #1e40af 85%, #1e3a8a 100%)',
                boxShadow: 'inset -12px 0 24px rgba(0,0,0,0.35), inset 6px 0 12px rgba(255,255,255,0.25), 0 0 0 1px rgba(0,0,0,0.1)',
                transformStyle: 'preserve-3d',
              }}
            >
              {/* Enhanced tube shine effect */}
              <motion.div
                className="absolute inset-0 opacity-50"
                animate={{
                  backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
                }}
                transition={{
                  duration: 8,
                  repeat: Infinity,
                  ease: 'linear',
                }}
                style={{
                  background: 'linear-gradient(90deg, transparent 25%, rgba(255,255,255,0.3) 40%, rgba(255,255,255,0.6) 50%, rgba(255,255,255,0.3) 60%, transparent 75%)',
                  backgroundSize: '200% 100%',
                }}
              />

              {/* Tube end cap top - enhanced 3D */}
              <div
                className="absolute top-0 left-0 right-0 h-4"
                style={{
                  background: 'radial-gradient(ellipse at center, #2563eb 0%, #1e40af 50%, #1e3a8a 100%)',
                  borderRadius: '0 0 50% 50% / 0 0 100% 100%',
                  boxShadow: 'inset 0 -2px 4px rgba(0,0,0,0.3), 0 2px 4px rgba(0,0,0,0.2)',
                }}
              />

              {/* Tube end cap bottom - enhanced 3D */}
              <div
                className="absolute bottom-0 left-0 right-0 h-4"
                style={{
                  background: 'radial-gradient(ellipse at center, #2563eb 0%, #1e40af 50%, #1e3a8a 100%)',
                  borderRadius: '50% 50% 0 0 / 100% 100% 0 0',
                  boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.3), 0 -2px 4px rgba(0,0,0,0.2)',
                }}
              />

              {/* Tube center logo with animation */}
              <motion.div
                className="relative z-10 w-12 h-12 lg:w-14 lg:h-14 rounded-full flex items-center justify-center"
                animate={{
                  rotate: [0, 5, -5, 0],
                  scale: [1, 1.05, 1],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.1) 100%)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.4), inset 0 2px 4px rgba(255,255,255,0.4), inset 0 -2px 4px rgba(0,0,0,0.2)',
                  border: '2px solid rgba(255,255,255,0.2)',
                }}
              >
                <SparklesIcon className="w-6 h-6 lg:w-7 lg:h-7 text-white drop-shadow-lg" />
              </motion.div>

              {/* Tube vertical highlights */}
              <div
                className="absolute top-4 bottom-4 left-1/4 w-px opacity-30"
                style={{
                  background: 'linear-gradient(180deg, transparent 0%, rgba(255,255,255,0.6) 50%, transparent 100%)',
                }}
              />
              <div
                className="absolute top-4 bottom-4 right-1/4 w-px opacity-20"
                style={{
                  background: 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.4) 50%, transparent 100%)',
                }}
              />
            </motion.div>

            {/* Right Side - Enhanced 3D Unrolled Paper with Spring Animation */}
            <animated.div
              key={`paper-${selectedPeriod}-${activeCustomRange?.start || ''}`}
              className="flex-1 relative overflow-hidden"
              style={{
                ...paperSpring,
                background: 'linear-gradient(135deg, #ffffff 0%, #fefefe 30%, #fcfcfd 60%, #f8fafc 100%)',
                boxShadow: `
                  inset 6px 0 24px rgba(0,0,0,0.14),
                  inset 0 3px 6px rgba(0,0,0,0.03),
                  0 6px 12px rgba(0,0,0,0.05),
                  0 2px 4px rgba(0,0,0,0.03)
                `,
                transformOrigin: 'left center',
                transformStyle: 'preserve-3d',
                borderRadius: '0 24px 24px 0',
              }}
            >
              {/* Enhanced paper texture overlay */}
              <div
                className="absolute inset-0 opacity-[0.05] pointer-events-none"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.95' numOctaves='5' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
                }}
              />

              {/* Left edge curl - Enhanced 3D depth effect */}
              <div
                className="absolute top-0 left-0 bottom-0 w-12"
                style={{
                  background: 'linear-gradient(90deg, rgba(0,0,0,0.18) 0%, rgba(0,0,0,0.12) 20%, rgba(0,0,0,0.06) 40%, rgba(0,0,0,0.02) 70%, transparent 100%)',
                }}
              />

              {/* Paper curl highlight */}
              <div
                className="absolute top-0 left-0 bottom-0 w-1"
                style={{
                  background: 'linear-gradient(180deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.6) 50%, rgba(255,255,255,0.4) 100%)',
                }}
              />

              {/* Top edge highlight for 3D */}
              <div
                className="absolute top-0 left-0 right-0 h-1"
                style={{
                  background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.9) 15%, rgba(255,255,255,0.9) 85%, transparent 100%)',
                }}
              />

              {/* Bottom subtle shadow for depth */}
              <div
                className="absolute bottom-0 left-12 right-0 h-2"
                style={{
                  background: 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.04) 100%)',
                }}
              />

              {/* Paper edge waves for realism */}
              <motion.div
                className="absolute top-0 right-0 bottom-0 w-px"
                animate={{
                  opacity: [0.3, 0.5, 0.3],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
                style={{
                  background: 'linear-gradient(180deg, transparent 5%, rgba(0,0,0,0.08) 50%, transparent 95%)',
                }}
              />

              {/* Content - Clean & Minimal */}
              <div className="relative z-10 px-10 lg:px-16 py-10 lg:py-12">
                <div className="flex flex-col items-center lg:items-start text-center lg:text-left">

                  {/* Main Title with enhanced animation */}
                  <motion.h1
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8, duration: 0.7, type: 'spring', bounce: 0.3 }}
                    className="text-4xl lg:text-5xl font-extrabold bg-gradient-to-r from-slate-900 via-blue-900 to-slate-900 bg-clip-text text-transparent mb-3"
                  >
                    Sales Toast Analytics
                  </motion.h1>

                  <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1, duration: 0.7 }}
                    className="text-base lg:text-lg text-slate-600 font-medium flex items-center gap-2"
                  >
                    <span className="inline-block w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                    Real-time insights • {formatDateLabel()}
                  </motion.p>

                </div>
              </div>
            </animated.div>
          </div>

          {/* Bottom accent strip */}
          <div
            className="h-1"
            style={{
              background: 'linear-gradient(90deg, #1e40af 0%, #2563eb 20%, transparent 20%)',
            }}
          />
        </div>

        {/* Subtle blue glow */}
        <div className="absolute -bottom-4 left-0 w-24 h-8 bg-blue-500/20 rounded-full blur-2xl pointer-events-none" />
      </motion.div>



      {/* Diamond Navigation Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        className="mb-12"
      >
        {/* Main Navigation Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
          {/* Left: Diamond Navigation */}
          <div className="flex justify-center">
            <DonutNavigation
              activeSection={activeSection}
              onSectionChange={handleSectionChange}
            />
          </div>

          {/* Right: Section Content */}
          <div>
            <SectionContent
              section={activeSection}
              onSubsectionClick={handleSubsectionClick}
            />
          </div>
        </div>
      </motion.div>


      {/* ALL 16 SECTIONS - Rich animated visualizations */}
      <div className="space-y-16 mb-16">
        <EnhancedSections
          period={selectedPeriod}
          startDate={activeCustomRange?.start}
          endDate={activeCustomRange?.end}
          isDemoMode={isDemoMode}
          overview={overview}
          salesmanPerformance={salesmanPerformance}
          dealsByPeriod={dealsByPeriod}
          clientsByPeriod={clientsByPeriod}
          revenueTrends={revenueTrends}
          topPerformers={topPerformers}
          activitySummary={activitySummary}
          stockAnalytics={stockAnalytics}
          productAnalytics={productAnalytics}
          supplierAnalytics={supplierAnalytics}
        />
      </div>

      {/* Custom Date Picker Modal */}
      <AnimatePresence>
        {showDatePicker && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowDatePicker(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-neutral-900">Custom Date Range</h3>
                <button
                  onClick={() => setShowDatePicker(false)}
                  className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
                >
                  <XMarkIcon className="w-5 h-5 text-neutral-500" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">From Date</label>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    className="w-full px-4 py-3 border border-blue-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">To Date</label>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    className="w-full px-4 py-3 border border-blue-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowDatePicker(false)}
                  className="flex-1 px-4 py-3 border border-slate-300 rounded-xl text-slate-700 font-medium hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleApplyCustomRange}
                  disabled={!customStartDate || !customEndDate}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-500 to-blue-700 text-white rounded-xl font-medium hover:from-blue-600 hover:to-blue-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-500/30"
                >
                  Apply Range
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Data Loading Overlay */}
      <AnimatePresence>
        {dataLoading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-white/60 backdrop-blur-sm z-40 flex items-center justify-center pointer-events-none"
          >
            <div className="bg-white rounded-2xl shadow-xl p-6 flex items-center gap-4">
              <Spinner size="md" />
              <span className="text-neutral-600 font-medium">Loading data...</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

    </PageTransition>
  );
};

export default AdminDashboard;
